import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../helpers";
import { useParams } from "react-router-dom";
import { safe_get } from "../../../report-visuals";
import Select, { OptionProps, MultiValueProps } from "react-select";
import { RegionFilter } from "./RegionFilter";
import Chip from "@mui/material/Chip";
import { UserDisplay } from "../components/index";
import { getSurveysFromStorage } from "../utils/localStorageUtils";
import { FILTER_DEBOUNCE_DELAY } from "../utils/constants";

interface InlineFiltersProps {
    onFiltersChange: (filters: {
        userList: number[];
        pivLevel1: string[];
        surveys: any[];
    }) => void;
    isIntegrated?: boolean;
}

export const InlineFilters: React.FC<InlineFiltersProps> = observer(
    ({ onFiltersChange, isIntegrated = false }) => {
        const { taskManagerStore, projectStore } = useStore();
        const { users } = taskManagerStore;
        const { task_filters } = taskManagerStore;
        const { projectid } = useParams();
        const [surveys, setSurveys] = useState<any[]>([]);

        const hierarchy = projectStore.getProjectProperty("hierarchy", {}) || [];

        // Initialize surveys from storage
        useEffect(() => {
            const storedSurveys = getSurveysFromStorage();
            setSurveys(storedSurveys);
        }, [projectid]);

        // Get current filter values from store
        const currentSurveys = useMemo(() => {
            const surveyIds = safe_get(task_filters, "surveys", []);
            if (Array.isArray(surveyIds) && surveys.length > 0) {
                return surveyIds
                    .map((id: number) => surveys.find((s) => s.id === id))
                    .filter(Boolean);
            }
            return [];
        }, [task_filters.surveys, surveys]);

        const currentUsers = useMemo(() => {
            const userIds = safe_get(task_filters, "userList", []);
            if (Array.isArray(userIds) && users && users.length > 0) {
                return userIds
                    .map((id: number) => users.find((u) => u.id === id))
                    .filter(Boolean);
            }
            return [];
        }, [task_filters.userList, users]);

        const currentLevel1 = useMemo(() => {
            return safe_get(task_filters, "pivLevel1", []) || [];
        }, [task_filters.pivLevel1]);

        // Debounced filter change handler
        const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
        const applyFilters = useCallback((userList: number[], pivLevel1: string[], surveyList: number[]) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                onFiltersChange({
                    userList,
                    pivLevel1,
                    surveys: surveyList,
                });
            }, FILTER_DEBOUNCE_DELAY);
        }, [onFiltersChange]);

        // Survey options
        const surveyOptions = useMemo(() => {
            return surveys.map((survey) => ({
                label: survey.name,
                value: survey.id,
            }));
        }, [surveys]);

        const selectedSurveyOptions = useMemo(() => {
            return surveyOptions.filter((opt) =>
                currentSurveys.some((s) => s.id === opt.value)
            );
        }, [surveyOptions, currentSurveys]);

        // User options
        const userOptions = useMemo(() => {
            return users.map((user) => ({
                label: user.name || "",
                value: user.id,
                user: user,
            }));
        }, [users]);

        const selectedUserOptions = useMemo(() => {
            return userOptions.filter((opt) =>
                currentUsers.some((u) => u.id === opt.value)
            );
        }, [userOptions, currentUsers]);

        // Handlers
        const handleSurveyChange = useCallback((val: any) => {
            const selected = Array.isArray(val) ? val : val ? [val] : [];
            const surveyObjects = selected.map((opt) =>
                surveys.find((s) => s.id === opt.value)
            ).filter(Boolean);
            const surveyIds = surveyObjects.map((s) => s.id);
            applyFilters(
                currentUsers.map((u) => u.id),
                currentLevel1,
                surveyIds
            );
        }, [surveys, currentUsers, currentLevel1, applyFilters]);

        const handleUserChange = useCallback((val: any) => {
            const selected = Array.isArray(val) ? val : val ? [val] : [];
            const userObjects = selected.map((opt) =>
                users.find((u) => u.id === opt.value)
            ).filter(Boolean);
            const userIds = userObjects.map((u) => u.id);
            applyFilters(
                userIds,
                currentLevel1,
                currentSurveys.map((s) => s.id)
            );
        }, [users, currentLevel1, currentSurveys, applyFilters]);

        const handleLevel1Change = useCallback((nodes: string[]) => {
            applyFilters(
                currentUsers.map((u) => u.id),
                nodes,
                currentSurveys.map((s) => s.id)
            );
        }, [currentUsers, currentSurveys, applyFilters]);

        const handleSurveyDelete = useCallback((surveyId: number) => {
            const updatedSurveys = currentSurveys.filter((s) => s.id !== surveyId);
            applyFilters(
                currentUsers.map((u) => u.id),
                currentLevel1,
                updatedSurveys.map((s) => s.id)
            );
        }, [currentSurveys, currentUsers, currentLevel1, applyFilters]);

        const handleUserDelete = useCallback((userId: number) => {
            const updatedUsers = currentUsers.filter((u) => u.id !== userId);
            applyFilters(
                updatedUsers.map((u) => u.id),
                currentLevel1,
                currentSurveys.map((s) => s.id)
            );
        }, [currentUsers, currentLevel1, currentSurveys, applyFilters]);

        const handleMultiValueRemove = useCallback((e: React.MouseEvent<HTMLSpanElement>, removeProps: any) => {
            e.stopPropagation();
            if (removeProps.onClick) {
                const syntheticEvent = {
                    ...e,
                    currentTarget: e.currentTarget.parentElement as HTMLDivElement,
                } as React.MouseEvent<HTMLDivElement>;
                removeProps.onClick(syntheticEvent);
            }
        }, []);

        // Custom Option component for react-select
        const CustomOption = useCallback((props: OptionProps<any>) => {
            const { data, innerRef, innerProps } = props;
            return (
                <div ref={innerRef} {...innerProps} className="py-1 px-2">
                    <UserDisplay
                        user={data.user}
                        avatarSize="xs"
                        nameClassName="font-medium"
                        phoneClassName="text-slate-500"
                    />
                </div>
            );
        }, []);

        // Custom MultiValue component for react-select
        const CustomMultiValue = useCallback((props: MultiValueProps<any>) => {
            const { data, innerProps, removeProps } = props;
            return (
                <div
                    {...innerProps}
                    className="inline-flex items-center bg-indigo-100 rounded-full py-0.5 px-2 m-0.5 text-xs text-indigo-700"
                >
                    <UserDisplay
                        user={data.user}
                        avatarSize="xs"
                        showPhone={false}
                        nameClassName="font-medium"
                    />
                    <span
                        className="ml-1 cursor-pointer text-[10px] inline-flex items-center"
                        onClick={(e) => handleMultiValueRemove(e, removeProps)}
                    >
                        <i className="fas fa-times" />
                    </span>
                </div>
            );
        }, [handleMultiValueRemove]);

        // Cleanup debounce timer on unmount
        useEffect(() => {
            return () => {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }
            };
        }, []);

        return (
            <div className={isIntegrated ? "" : "bg-white rounded-xl border border-slate-200 mb-4"}>
                <div className={isIntegrated ? "" : "px-4 pb-4"}>
                    <div className={isIntegrated ? "flex items-center gap-3 flex-wrap" : "flex items-center gap-4 flex-wrap"}>
                        <div className={`flex-1 ${isIntegrated ? "flex items-center gap-3 flex-wrap" : "grid grid-cols-1 md:grid-cols-3 gap-4"}`}>
                            {/* Surveys Filter */}
                            <div className={isIntegrated ? "min-w-[350px] sm:min-w-[250px]" : ""}>
                                {!isIntegrated && (
                                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                                        Surveys
                                    </label>
                                )}
                                <Select
                                    value={selectedSurveyOptions}
                                    options={surveyOptions}
                                    onChange={handleSurveyChange}
                                    placeholder="Select surveys..."
                                    isMulti
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: "0.5rem",
                                            borderColor: "#e2e8f0",
                                            minHeight: "40px",
                                            fontSize: "14px",
                                            boxShadow: "none",
                                            "&:hover": { borderColor: "#cbd5e1" },
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            borderRadius: "0.5rem",
                                            overflow: "hidden",
                                        }),
                                    }}
                                />
                                {!isIntegrated && currentSurveys.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {currentSurveys.slice(0, 3).map((survey) => (
                                            <Chip
                                                key={survey.id}
                                                label={survey.name}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                onDelete={() => handleSurveyDelete(survey.id)}
                                            />
                                        ))}
                                        {currentSurveys.length > 3 && (
                                            <Chip
                                                label={`+${currentSurveys.length - 3}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Users Filter */}
                            <div className={isIntegrated ? "min-w-[350px] sm:min-w-[250px]" : ""}>
                                {!isIntegrated && (
                                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                                        Assigned To
                                    </label>
                                )}
                                <Select
                                    value={selectedUserOptions}
                                    options={userOptions}
                                    onChange={handleUserChange}
                                    placeholder="Select users..."
                                    isMulti
                                    isSearchable
                                    components={{
                                        Option: CustomOption,
                                        MultiValue: CustomMultiValue,
                                    }}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: "0.5rem",
                                            borderColor: "#e2e8f0",
                                            minHeight: "40px",
                                            fontSize: "14px",
                                            boxShadow: "none",
                                            "&:hover": { borderColor: "#cbd5e1" },
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            borderRadius: "0.5rem",
                                            overflow: "hidden",
                                        }),
                                    }}
                                />
                                {!isIntegrated && currentUsers.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {currentUsers.slice(0, 2).map((user) => (
                                            <Chip
                                                key={user.id}
                                                label={user.name}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                onDelete={() => handleUserDelete(user.id)}
                                            />
                                        ))}
                                        {currentUsers.length > 2 && (
                                            <Chip
                                                label={`+${currentUsers.length - 2}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Level1 Filter */}
                            <div className={isIntegrated ? "min-w-[350px] sm:min-w-[450px]" : ""}>
                                {!isIntegrated && (
                                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                                        Region (Level 1)
                                    </label>
                                )}
                                <RegionFilter
                                    hierarchy={hierarchy}
                                    selectedLevel1={currentLevel1}
                                    onSelectionChange={handleLevel1Change}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);
